import { useState, useEffect } from 'react';
import Link from '@/lib/siteLink';
import { Utensils, ShoppingBag, Zap, MoreHorizontal, ArrowRight, Smartphone, Film, Plane, ExternalLink, Clock, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { couponApi, dealsApi, rechargeApi, formatPrice } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import AdSlot from '@/components/AdComponents';
import { CouponHeader, CouponFooter } from '@/components/CouponLayout';
import LatestArticles from '@/components/LatestArticles';
import { initGA, usePageTracking, storeUTMParams } from '@/lib/analytics';
import SEO from '@/components/SEO';
import { cdnUrl } from '@/components/OptimizedImage';

export default function CouponHome() {
  const { user } = useAuth();
  const [featuredCoupons, setFeaturedCoupons] = useState([]);
  const [latestDeals, setLatestDeals] = useState([]);
  const [rechargeOffers, setRechargeOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initGA('coupon');
    storeUTMParams();
  }, []);
  
  usePageTracking('coupon');

  useEffect(() => {
    loadCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCoupons = async () => {
    try {
      const response = await couponApi.list({ is_featured: true, limit: 6 });
      setFeaturedCoupons(Array.isArray(response.data) ? response.data : []);
      // Load syndicated deals (non-blocking)
      try { const dr = await dealsApi.latest(8); setLatestDeals(dr.data?.deals || []); } catch (err) { console.error('Failed to load deals:', err); }
      // Load recharge offers (non-blocking)
      try { const rr = await rechargeApi.listOffers(); setRechargeOffers(rr.data.offers || []); } catch (err) { console.error('Failed to load recharge offers:', err); }
    } catch (error) {
      console.error('Failed to load coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'food', label: 'Food & Dining', icon: Utensils, color: 'hsl(10, 80%, 55%)', image: 'https://images.unsplash.com/photo-1632898657999-ae6920976661?w=400' },
    { id: 'fashion', label: 'Fashion', icon: ShoppingBag, color: 'hsl(320, 80%, 55%)', image: 'https://images.unsplash.com/photo-1761370571873-5d869310d731?w=400' },
    { id: 'electronics', label: 'Electronics', icon: Smartphone, color: 'hsl(210, 90%, 50%)', image: 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=400' },
    { id: 'entertainment', label: 'Entertainment', icon: Film, color: 'hsl(280, 80%, 55%)', image: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=400' },
    { id: 'travel', label: 'Travel', icon: Plane, color: 'hsl(170, 80%, 40%)', image: 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=400' },
    { id: 'utility', label: 'Utility', icon: Zap, color: 'hsl(260, 90%, 60%)', image: 'https://images.unsplash.com/photo-1772812714422-cd5d342a786a?w=400' },
    { id: 'others', label: 'Others', icon: MoreHorizontal, color: 'hsl(150, 90%, 35%)', image: 'https://images.unsplash.com/photo-1511385348-a52b4a160dc2?w=400' },
  ];

  return (
    <div className="coupon-theme min-h-screen">
      <SEO
        title="Best Coupons, Deals & Offers in India"
        description="Save big with verified coupons, cashback offers, and live deals from top Indian brands. Flipkart, Amazon, Myntra, Swiggy and more."
        keywords="coupons India, deals, offers, cashback, promo codes, discount codes, Flipkart coupons, Amazon deals, online shopping India"
        canonical="https://metromandi.com/coupons"
      />
      <CouponHeader />

      {/* Hero Section */}
      <section className="coupon-hero pt-24 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-40 h-40 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-60 h-60 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-space-grotesk text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 animate-slide-up" data-testid="hero-title">
            Save Big on<br />
            <span className="text-[hsl(150,90%,70%)]">Every Purchase</span>
          </h1>
          <p className="text-lg text-white/80 max-w-xl mx-auto mb-10 animate-slide-up stagger-1" data-testid="hero-subtitle">
            Discover exclusive coupons and deals from top brands. Food, fashion, utilities, and more!
          </p>
          
          <Link to="/coupons/browse">
            <Button 
              size="lg" 
              className="bg-white text-[hsl(260,90%,50%)] hover:bg-gray-100 rounded-full px-8 font-semibold animate-slide-up stagger-2"
              data-testid="browse-all-btn"
            >
              Browse All Deals
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <LatestArticles site="coupon" title="Latest Deals & Savings Tips" basePath="/coupon/blog" />

      {/* Ad Slot - After Hero */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <AdSlot site="coupon" placement="hero_after" maxAds={1} />
      </div>

      {/* Categories */}
      <section className="py-16 -mt-10 relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="category-scroll pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/coupons/browse/${cat.id}`}
                className="flex-shrink-0 group"
                data-testid={`category-${cat.id}`}
              >
                <div className="w-40 bg-white rounded-2xl shadow-lg overflow-hidden coupon-card">
                  <div className="h-24 relative overflow-hidden">
                    <img
                      src={cdnUrl(cat.image, { width: 200 })}
                      alt={cat.label}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy" decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div 
                      className="absolute bottom-3 left-3 w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: cat.color }}
                    >
                      <cat.icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-space-grotesk font-semibold text-sm text-gray-900">{cat.label}</h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Coupons */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <h2 className="font-space-grotesk text-2xl sm:text-3xl font-bold text-gray-900" data-testid="featured-title">
              Hot Deals
            </h2>
            <Link to="/coupons/browse" className="text-[hsl(260,90%,60%)] font-medium text-sm hover:underline" data-testid="view-all-link">
              View All
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-50 rounded-2xl p-6">
                  <div className="flex gap-4">
                    <div className="w-20 h-20 skeleton rounded-xl" />
                    <div className="flex-1 space-y-3">
                      <div className="h-5 skeleton rounded w-3/4" />
                      <div className="h-4 skeleton rounded w-full" />
                      <div className="h-8 skeleton rounded w-1/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCoupons.map((coupon, index) => (
                <Link
                  key={coupon.id}
                  to={`/coupons/deal/${coupon.id}`}
                  className="coupon-ticket p-6 coupon-card animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`coupon-card-${coupon.id}`}
                >
                  <div className="flex gap-4">
                    <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={cdnUrl(coupon.image_url || 'https://images.unsplash.com/photo-1632898657999-ae6920976661?w=200', { width: 200 })}
                        alt={coupon.brand_name}
                        className="w-full h-full object-cover"
                        loading="lazy" decoding="async"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-[hsl(260,90%,60%)] uppercase tracking-wide">
                        {coupon.brand_name}
                      </span>
                      <h3 className="font-space-grotesk font-semibold text-gray-900 mt-1 line-clamp-2">
                        {coupon.title}
                      </h3>
                      <div className="mt-2">
                        <span className="discount-badge text-sm">
                          {coupon.discount_type === 'percentage' ? (
                            <>{coupon.discount_value}% OFF</>
                          ) : (
                            <>₹{coupon.discount_value} OFF</>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  {coupon.min_purchase && (
                    <p className="text-xs text-gray-500 mt-4 pt-4 border-t border-dashed border-gray-200">
                      Min. purchase: {formatPrice(coupon.min_purchase)}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Free Recharge Section */}
      <section className="py-12 bg-[hsl(var(--coupon-background))]" data-testid="free-recharge-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Smartphone className="w-6 h-6 text-[hsl(260,90%,60%)]" />
              <h2 className="font-space-grotesk text-2xl font-bold text-gray-900">Free Recharge</h2>
            </div>
            <Link to="/coupons/free-recharge" className="text-[hsl(260,90%,60%)] hover:underline font-medium text-sm flex items-center gap-1" data-testid="view-all-recharge">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {rechargeOffers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rechargeOffers.slice(0, 3).map((offer, index) => {
                const spotsLeft = Math.max(0, offer.max_claims - (offer.claims_count || 0));
                return (
                  <Link
                    key={offer.id}
                    to="/coupons/free-recharge"
                    className="coupon-ticket p-6 coupon-card animate-slide-up hover:shadow-lg transition-shadow"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`home-recharge-${offer.id}`}
                  >
                    <div className="flex gap-4">
                      <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
                        <Smartphone className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium text-[hsl(260,90%,60%)] uppercase tracking-wide">FREE RECHARGE</span>
                        <h3 className="font-space-grotesk font-semibold text-gray-900 mt-1 line-clamp-2">{offer.title}</h3>
                        <div className="mt-2">
                          <span className="discount-badge text-sm">₹{offer.recharge_amount} FREE</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-dashed border-gray-200">
                      <p className="text-xs text-gray-500">{spotsLeft > 0 ? `${spotsLeft} spots remaining` : 'Fully claimed'}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="coupon-ticket p-8 text-center">
              <Smartphone className="w-10 h-10 text-[hsl(260,90%,60%)] mx-auto mb-3 opacity-40" />
              <p className="text-gray-500 text-sm mb-4">Free recharge offers coming soon!</p>
              <Link to="/coupons/free-recharge">
                <Button className="bg-[hsl(260,90%,60%)] hover:bg-[hsl(260,90%,50%)] text-white text-sm" data-testid="recharge-cta-btn">
                  Check Recharge Page <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Live Deals - Syndicated */}
      {latestDeals.length > 0 && (
        <section className="py-12 bg-gray-50" data-testid="live-deals-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-[hsl(260,90%,60%)]" />
                <h2 className="font-space-grotesk text-2xl font-bold text-gray-900">Live Deals & Offers</h2>
              </div>
              <Link to="/coupons/live-deals" className="text-[hsl(260,90%,60%)] font-medium text-sm hover:underline flex items-center gap-1" data-testid="view-all-deals">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {latestDeals.map((deal) => (
                <a key={deal.id} href={deal.url} target="_blank" rel="noopener noreferrer nofollow"
                  className="group bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all hover:border-[hsl(260,90%,80%)]"
                  data-testid={`deal-card-${deal.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-[hsl(260,90%,60%)] bg-[hsl(260,90%,97%)] px-2 py-0.5 rounded-full">{deal.store}</span>
                        {deal.discount && <span className="text-xs font-bold text-green-600">{deal.discount}</span>}
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-[hsl(260,90%,50%)] transition-colors">{deal.title}</h3>
                      {deal.coupon_code && (
                        <div className="mt-2 bg-yellow-50 border border-dashed border-yellow-300 rounded px-2 py-1 inline-block">
                          <span className="text-xs font-mono font-bold text-yellow-800">{deal.coupon_code}</span>
                        </div>
                      )}
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[hsl(260,90%,60%)] flex-shrink-0 mt-1" />
                  </div>
                  <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">{deal.source_name}</span>
                    <span className="text-[10px] text-gray-400 capitalize">{deal.category}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ad Slot - Before Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <AdSlot site="coupon" placement="before_footer" maxAds={1} />
      </div>

      <CouponFooter />
    </div>
  );
}
